// React hook to build a TrackingContext from Redux state.
// Use this in any component that needs to fire tracking events.
//
// Example:
//   const ctx = useTrackingContext()
//   trackInsightViewed({ brand_id, account_id: ctx.accountId, kpi }, ctx)

import { useAppSelector } from '../state/hooks'
import { selectSuperuser, selectAccountId } from '../state/appSlice'
import { TrackingContext } from './tracking'

/**
 * Returns a TrackingContext for the currently authenticated user.
 * All tracking calls are no-ops if the user is a superuser or not authenticated.
 */
export function useTrackingContext(): TrackingContext {
  const superuser = useAppSelector(selectSuperuser)
  const accountId = useAppSelector(selectAccountId)
  return { accountId, superuser }
}

/**
 * Returns whether tracking is enabled for the current user.
 * Useful for conditionally skipping expensive operations before calling track functions.
 */
export function useIsTrackingEnabled(): boolean {
  const superuser = useAppSelector(selectSuperuser)
  const accountId = useAppSelector(selectAccountId)
  return !!accountId && !superuser
}
